import type { TFunction } from 'i18next';
import writeXlsxFile, { Cell, Columns, Row, SheetData } from 'write-excel-file';

import { CycleAnalytics } from '@treams/treams-analytics';
import { LocalizedString } from '@treams/treams-network';

import {
	CycleColumn,
	buildEmployeeRows,
	buildManagerExcelColumns,
	buildMomentColumns,
	managerName,
	statusKeysForMoment,
} from './cycleColumns';

type Localize = <T extends { languageID: string }>(items?: T[] | null, defaultValue?: T) => T;

const HEADER_STYLE = {
	fontWeight: 'bold',
	backgroundColor: '#eeeeee',
} as const;

// Fractions are written as three columns (numerator, denominator, percentage),
// matching the metric details export, so Excel can sort and sum them.
const FRACTION_WIDTHS = [8, 8, 8];

const COLUMN_WIDTHS: Record<string, number> = {
	employee: 30,
	manager: 30,
	departments: 40,
	moment: 30,
	startDate: 16,
	endDate: 16,
	remaining: 16,
	duration: 16,
};

const DEFAULT_COLUMN_WIDTH = 14;

// Excel sheet (tab) names cap at 31 chars, forbid : \ / ? * [ ] and must be unique.
export function toSheetName(rawName: string, usedNames: Set<string>): string {
	const sanitized =
		(rawName || 'Sheet')
			.replace(/[:\\/?*[\]]/g, ' ')
			.trim()
			.slice(0, 31) || 'Sheet';

	let candidate = sanitized;
	let suffix = 1;

	while (usedNames.has(candidate.toLowerCase())) {
		const tail = ` (${suffix})`;
		candidate = `${sanitized.slice(0, 31 - tail.length)}${tail}`;
		suffix += 1;
	}

	usedNames.add(candidate.toLowerCase());

	return candidate;
}

function headerRows<RowData>(columns: CycleColumn<RowData>[], t: TFunction): SheetData {
	const hasFraction = columns.some((column) => column.type === 'fraction');

	const first: Row = columns.flatMap((column): Row => {
		if (column.type === 'fraction') {
			return [
				{
					...HEADER_STYLE,
					value: column.header,
					span: 3,
					bottomBorderStyle: hasFraction ? undefined : 'thick',
				},
				null,
				null,
			];
		}

		return [
			{
				...HEADER_STYLE,
				value: column.header,
				bottomBorderStyle: 'thick',
				rowSpan: hasFraction ? 2 : 1,
				wrap: true,
			},
		];
	});

	if (!hasFraction) {
		return [first];
	}

	const second: Row = columns.flatMap((column): Row => {
		if (column.type === 'fraction') {
			return [
				t('analytics.export.columns.fraction.numerator'),
				t('analytics.export.columns.fraction.denominator'),
				t('analytics.export.columns.fraction.percentage'),
			].map((value) => ({ ...HEADER_STYLE, value, bottomBorderStyle: 'thick' }));
		}

		return [null];
	});

	return [first, second];
}

function dataRow<RowData>(columns: CycleColumn<RowData>[], row: RowData): Row {
	return columns.flatMap((column): Row => {
		if (column.type === 'fraction') {
			const fraction = column.value(row);

			if (!fraction) {
				return [{ value: column.emptyText ?? '' }, null, null];
			}

			const { value, total } = fraction;

			return [
				{ type: Number, value },
				{ type: Number, value: total },
				{ type: Number, value: total > 0 ? value / total : 0, format: '0.0%' },
			];
		}

		const cell: Cell = { value: column.value(row), wrap: column.id === 'departments' };

		return [cell];
	});
}

function columnWidths<RowData>(columns: CycleColumn<RowData>[]): Columns {
	return columns.flatMap((column) =>
		column.type === 'fraction'
			? FRACTION_WIDTHS.map((width) => ({ width }))
			: [{ width: COLUMN_WIDTHS[column.id] ?? DEFAULT_COLUMN_WIDTH }],
	);
}

interface Sheet {
	name: string;
	data: SheetData;
	columns: Columns;
}

function buildSheet<RowData>(
	name: string,
	columns: CycleColumn<RowData>[],
	rows: RowData[],
	t: TFunction,
): Sheet {
	return {
		name,
		data: [...headerRows(columns, t), ...rows.map((row) => dataRow(columns, row))],
		columns: columnWidths(columns),
	};
}

// One workbook mirroring the cycle analytics page: a sheet with the moments
// overview, then one sheet per moment with that moment's employee statuses.
export function buildCycleWorkbook({
	cycleAnalytics,
	activeGroupIDs,
	t,
	localize,
}: {
	cycleAnalytics: CycleAnalytics;
	activeGroupIDs: Set<string>;
	t: TFunction;
	localize: Localize;
}): Sheet[] {
	const usedNames = new Set<string>();
	const localizeTitle = (title: LocalizedString) => localize(title).text ?? '';

	const momentsSheet = buildSheet(
		toSheetName(t('analytics.cycles.moments.title'), usedNames),
		buildMomentColumns({ interval: cycleAnalytics.cycleTemplate.interval, t, localize }),
		cycleAnalytics.moments,
		t,
	);

	const momentSheets = cycleAnalytics.moments.map((cycleMoment) => {
		const rowData = buildEmployeeRows(
			cycleAnalytics.employees,
			cycleMoment.id,
			activeGroupIDs,
		).sort((a, b) => managerName(a.user).localeCompare(managerName(b.user)));

		return buildSheet(
			toSheetName(localizeTitle(cycleMoment.title), usedNames),
			buildManagerExcelColumns(
				statusKeysForMoment(cycleAnalytics.manager, cycleMoment.id, cycleMoment.type),
				t,
			),
			rowData,
			t,
		);
	});

	return [momentsSheet, ...momentSheets];
}

export async function exportCycleAnalyticsToExcel(params: {
	cycleAnalytics: CycleAnalytics;
	activeGroupIDs: Set<string>;
	t: TFunction;
	localize: Localize;
	fileName: string;
}): Promise<void> {
	const sheets = buildCycleWorkbook(params);

	await writeXlsxFile(
		sheets.map((sheet) => sheet.data),
		{
			sheets: sheets.map((sheet) => sheet.name),
			columns: sheets.map((sheet) => sheet.columns),
			fileName: params.fileName,
		},
	);
}
